import React, { useCallback, useMemo } from 'react';
import classNames from 'classnames';
import { ExtractPropTypes } from 'types';
import { Select } from 'ncoded-component-library';
import { FieldRenderProps } from 'react-final-form';
import { OptionValue } from 'ncoded-component-library/build/components/molecules/Select/Select.component';
import FieldWrapper from 'components/FieldWrapper';
import { FieldWrapperProps } from 'components/FieldWrapper/FieldWrapper.component';

import './SelectField.styles.scss';

type SelectComponentProps = ExtractPropTypes<typeof Select>;

export type SelectFieldProps = FieldWrapperProps &
  SelectComponentProps &
  FieldRenderProps<string, HTMLElement> & {
    showInitialError?: boolean;
    customOnChange?: (value: any, isValid: boolean) => void;
  };

const SelectField: React.FC<SelectFieldProps> = (props) => {
  const {
    input: { onChange, ...restInput },
    meta: { touched, error },
    className,
    options,
    showInitialError = false,
    customOnChange,
    ...rest
  } = props;

  const hasError = useMemo(
    () => (touched || showInitialError) && error,
    [error, showInitialError, touched],
  );

  const classes = classNames(
    'select-field',
    { 'select-field--error': hasError },
    className,
  );

  const handleOnChange = useCallback(
    (values: OptionValue<any>) => {
      onChange(values.value);
      customOnChange?.(values.value, !hasError);
    },
    [hasError, onChange, customOnChange],
  );

  return (
    <FieldWrapper
      className={classes}
      error={error}
      hasError={hasError}
      {...restInput}
      {...rest}
    >
      <Select
        {...rest}
        {...restInput}
        multiple={false}
        hasError={hasError}
        options={options}
        searchInputProps={{
          type: 'search',
          autoComplete: 'chrome-off',
        }}
        onChange={handleOnChange}
        onClose={restInput.onBlur}
      />
    </FieldWrapper>
  );
};

export default SelectField;
